import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import {
  cancelValetSessionReturn,
  claimValetSession,
  confirmValetSessionParked,
  confirmValetSessionReturned,
  dismissValetSession,
  ensureValetSessionForBooking,
  flagBookingVip,
  markValetCollected,
  markValetReady,
  markValetVehicleLocated,
  requestAndStartValetReturn,
  requestValetSessionReturn,
  returnValetPickupToQueue,
  returnValetSessionToQueue,
  scheduleValetSessionReturn,
  startValetEnRoute,
  startValetParking,
  startValetReturn,
  updateBookingGuestInfo,
} from "./queries";

/*
 * The two writes behind the intake flow. Kept out of ConciergeDashboard so the
 * dashboard reads as a state machine rather than as Apollo wiring.
 */
export default function useValetSessionActions(facilityId, { runnerScoped = false } = {}) {
  const queueQuery = runnerScoped
    ? "valetRunnerParkingQueue"
    : "valetParkingQueue";
  const lotQuery = runnerScoped
    ? "valetRunnerParkedSessions"
    : "valetParkedSessions";

  const [runEnsure, { loading: ensuring }] = useMutation(
    ensureValetSessionForBooking,
  );
  const [runClaim, { loading: claimingMutation }] = useMutation(
    claimValetSession,
  );
  const [runDismiss, { loading: dismissing }] = useMutation(
    dismissValetSession,
  );
  const [runUpdateGuestInfo, { loading: savingGuestInfo }] = useMutation(
    updateBookingGuestInfo,
  );
  const [runFlagVip, { loading: flaggingVip }] = useMutation(flagBookingVip);
  const [runStartParking, { loading: startingParking }] = useMutation(
    startValetParking,
  );
  const [runReturnToQueue, { loading: returningToQueue }] = useMutation(
    returnValetSessionToQueue,
  );
  const [runConfirmParked, { loading: confirmingParked }] = useMutation(
    confirmValetSessionParked,
  );
  const [runSchedule, { loading: scheduling }] = useMutation(
    scheduleValetSessionReturn,
  );
  const [runCancelReturn, { loading: cancellingReturn }] = useMutation(
    cancelValetSessionReturn,
  );
  const [runRequestReturn, { loading: requestingReturn }] = useMutation(
    requestValetSessionReturn,
  );
  const [runStartReturn, { loading: startingReturn }] = useMutation(
    startValetReturn,
  );
  const [runEnRoute, { loading: startingEnRoute }] = useMutation(
    startValetEnRoute,
  );
  const [runVehicleLocated, { loading: locatingVehicle }] = useMutation(
    markValetVehicleLocated,
  );
  const [runCollected, { loading: collecting }] = useMutation(
    markValetCollected,
  );
  const [runReady, { loading: readying }] = useMutation(markValetReady);
  const [runRequestAndStartReturn, { loading: scanAccepting }] = useMutation(
    requestAndStartValetReturn,
  );
  const [runReturnPickup, { loading: returningPickup }] = useMutation(
    returnValetPickupToQueue,
  );
  const [runConfirmReturned, { loading: confirmingReturned }] = useMutation(
    confirmValetSessionReturned,
  );

  /*
   * Returns the valet session for a booking, opening one if the car does not
   * have an active session yet. Pass valetSessionId when the car is already on
   * the forecourt - it is the deterministic path and skips the plate lookup.
   */
  const ensureSession = useCallback(
    async ({ bookingId, valetSessionId = null }) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!bookingId) throw new Error("No booking to attach to the queue.");

      const { data } = await runEnsure({
        variables: { bookingId, facilityId, valetSessionId },
      });

      return data?.ensureValetSessionForBooking ?? null;
    },
    [facilityId, runEnsure],
  );

  const claimSession = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to claim.");

      const { data } = await runClaim({
        variables: { valetSessionId, facilityId },

        /* CLAIMED is what puts the car on the runner's to-park list, and the
         * started booking is what drops it off Expected Arrivals. */
        refetchQueries: (result) =>
          result.data?.claimValetSession?.bookingTransitioned
            ? [queueQuery, "expectedArrivals"]
            : [queueQuery],
      });

      return data?.claimValetSession ?? null;
    },
    [facilityId, runClaim, queueQuery],
  );

  const dismissSession = useCallback(
    async (valetSessionId, reason) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to dismiss.");

      const { data } = await runDismiss({
        variables: { valetSessionId, facilityId, reason: reason ?? null },
        refetchQueries: ["valetArrivalsQueue"],
        awaitRefetchQueries: true,
      });

      return data?.dismissValetSession ?? null;
    },
    [facilityId, runDismiss],
  );

  /*
   * Runner transitions. The mutations return the session, so the queue row is
   * patched in place - only the last one needs a refetch, because PARKED drops
   * the car out of the query's status filter altogether.
   */
  const startParking = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to park.");

      const { data } = await runStartParking({
        variables: { valetSessionId, facilityId },
      });

      return data?.startValetParking ?? null;
    },
    [facilityId, runStartParking],
  );

  const returnToQueue = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to return.");

      const { data } = await runReturnToQueue({
        variables: { valetSessionId, facilityId },
      });

      return data?.returnValetSessionToQueue ?? null;
    },
    [facilityId, runReturnToQueue],
  );

  const confirmParked = useCallback(
    async (valetSessionId, { lotId, bay, offsiteTicket } = {}) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to park.");

      const { data } = await runConfirmParked({
        variables: { valetSessionId, facilityId, lotId, bay, offsiteTicket },
        /* PARKED leaves one status filter and enters the other, so both lists
         * are stale: the car has to drop off the runner queue and appear on the
         * lot in the same beat, rather than on the next poll. */
        refetchQueries: [queueQuery, lotQuery],
        awaitRefetchQueries: true,
      });

      return data?.confirmValetSessionParked ?? null;
    },
    [facilityId, runConfirmParked, queueQuery, lotQuery],
  );

  /*
   * The desk calls a car up. REQUESTED is inside the lot query's filter, so that
   * row is patched in place by id - but it *enters* the runner queue's filter,
   * which would not know about the car until its next poll.
   */
  const requestReturn = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to call up.");

      const { data } = await runRequestReturn({
        variables: { valetSessionId, facilityId },
        refetchQueries: [queueQuery],
        awaitRefetchQueries: true,
      });

      return data?.requestValetSessionReturn ?? null;
    },
    [facilityId, runRequestReturn, queueQuery],
  );

  /*
   * A booked time puts the car in the runner queue straight away, due then, so
   * the queue is refetched the same way a pickup lodged for now is.
   */
  const scheduleReturn = useCallback(
    async (valetSessionId, requestedAt) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to schedule.");

      const { data } = await runSchedule({
        variables: { valetSessionId, facilityId, requestedAt },
        refetchQueries: [queueQuery],
        awaitRefetchQueries: true,
      });

      return data?.scheduleValetSessionReturn ?? null;
    },
    [facilityId, runSchedule, queueQuery],
  );

  /* The car leaves the runner queue's filter, which would not know until its
   * next poll. */
  const cancelReturn = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to change.");

      const { data } = await runCancelReturn({
        variables: { valetSessionId, facilityId },
        refetchQueries: [queueQuery],
        awaitRefetchQueries: true,
      });

      return data?.cancelValetSessionReturn ?? null;
    },
    [facilityId, runCancelReturn, queueQuery],
  );

  /*
   * Pickup transitions, the mirror of the parking ones. Same patch-in-place
   * behaviour, and the same refetch on the step that leaves the status filter.
   */
  const startReturn = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to fetch.");

      const { data } = await runStartReturn({
        variables: { valetSessionId, facilityId },
      });

      return data?.startValetReturn ?? null;
    },
    [facilityId, runStartReturn],
  );

  /*
   * The walk-up QR: called up and taken in one write, keyed on the booking
   * because that is all the guest's QR carries.
   */
  const requestAndStartReturn = useCallback(
    async (bookingId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!bookingId) throw new Error("No booking to fetch.");

      const { data } = await runRequestAndStartReturn({
        variables: { bookingId, facilityId },
        refetchQueries: [queueQuery],
        awaitRefetchQueries: true,
      });

      return data?.requestAndStartValetReturn ?? null;
    },
    [facilityId, runRequestAndStartReturn, queueQuery],
  );

  /*
   * The steps between taking a pickup and handing it over. None of them changes
   * which query the row belongs to, so Apollo patches the cached session by id
   * and no refetch is needed.
   */
  const advancePickup = useCallback(
    async (mutation, valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to advance.");

      const run = {
        startEnRoute: runEnRoute,
        markVehicleLocated: runVehicleLocated,
        markCollected: runCollected,
        markReady: runReady,
      }[mutation];

      if (!run) throw new Error(`Unknown pickup step ${mutation}.`);

      const { data } = await run({ variables: { valetSessionId, facilityId } });

      return Object.values(data ?? {})[0] ?? null;
    },
    [facilityId, runEnRoute, runVehicleLocated, runCollected, runReady],
  );

  const returnPickupToQueue = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to hand back.");

      const { data } = await runReturnPickup({
        variables: { valetSessionId, facilityId },
      });

      return data?.returnValetPickupToQueue ?? null;
    },
    [facilityId, runReturnPickup],
  );

  const confirmReturned = useCallback(
    async (valetSessionId) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!valetSessionId) throw new Error("No valet session to deliver.");

      const { data } = await runConfirmReturned({
        variables: { valetSessionId, facilityId },
        /* RETURNED is terminal: the row leaves the runner queue's filter and
         * belongs to no list, so the cached copy has to go. */
        refetchQueries: [queueQuery],
        awaitRefetchQueries: true,
      });

      return data?.confirmValetSessionReturned ?? null;
    },
    [facilityId, runConfirmReturned, queueQuery],
  );

  /*
   * Writes the phone / vehicle make onto the booking. Omitted keys are left
   * untouched server-side, so pass only what was captured.
   */
  const saveGuestInfo = useCallback(
    async ({ bookingId, guestPhone, carType }) => {
      if (!bookingId) throw new Error("No booking to save against.");

      const { data } = await runUpdateGuestInfo({
        variables: { bookingId, guestPhone, carType },
      });

      return data?.updateBookingGuestInfo ?? null;
    },
    [runUpdateGuestInfo],
  );

  const flagVip = useCallback(
    async ({ bookingId, vipContactIds }) => {
      if (!facilityId) throw new Error("No facility selected.");
      if (!bookingId) throw new Error("No booking to flag as VIP.");
      if (!vipContactIds?.length)
        throw new Error("No VIP contacts selected.");

      const { data } = await runFlagVip({
        variables: { bookingId, facilityId, vipContactIds },
      });

      return data?.flagBookingVip ?? null;
    },
    [facilityId, runFlagVip],
  );

  return {
    ensureSession,
    claimSession,
    dismissSession,
    saveGuestInfo,
    flagVip,
    startParking,
    returnToQueue,
    confirmParked,
    requestReturn,
    scheduleReturn,
    cancelReturn,
    startReturn,
    requestAndStartReturn,
    advancePickup,
    returnPickupToQueue,
    confirmReturned,
    // Split out as well as combined: the buttons need to say which write is in
    // flight, the queue only needs to know that one is.
    returningToQueue,
    confirmingParked,
    requestingReturn,
    scheduling,
    cancellingReturn,
    returningPickup,
    confirmingReturned,
    flaggingVip,
    dismissing,
    parkingBusy:
      startingParking ||
      returningToQueue ||
      confirmingParked ||
      requestingReturn ||
      scheduling ||
      cancellingReturn ||
      startingReturn ||
      scanAccepting ||
      startingEnRoute ||
      locatingVehicle ||
      collecting ||
      readying ||
      returningPickup ||
      confirmingReturned,
    busy:
      ensuring ||
      claimingMutation ||
      savingGuestInfo ||
      flaggingVip ||
      dismissing,
  };
}
